import {useCallback, useEffect, useRef, useState} from 'react'

const EDGE_TOLERANCE_PX = 2

export const useHorizontalScrollControls = (itemsCount: number, cardsPerScroll = 1) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const updateControls = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const {scrollLeft, clientWidth, scrollWidth} = container
    setCanScrollPrev(scrollLeft > EDGE_TOLERANCE_PX)
    setCanScrollNext(scrollLeft + clientWidth < scrollWidth - EDGE_TOLERANCE_PX)
  }, [])

  const getScrollStep = useCallback(() => {
    const container = containerRef.current
    if (!container) return 0

    const firstScrollableChild = Array.from(container.children).find((child) => {
      if (!(child instanceof HTMLElement)) return false
      if (child.tagName === 'SCRIPT') return false
      return child.getBoundingClientRect().width > 0
    }) as HTMLElement | undefined
    if (!firstScrollableChild) return 0

    const styles = window.getComputedStyle(container)
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0

    return (firstScrollableChild.getBoundingClientRect().width + gap) * cardsPerScroll
  }, [cardsPerScroll])

  const scrollByCard = useCallback(
    (direction: 'next' | 'prev') => {
      const container = containerRef.current
      if (!container) return

      const step = getScrollStep()
      if (!step) return

      container.scrollBy({
        left: direction === 'next' ? step : -step,
        behavior: 'smooth',
      })
    },
    [getScrollStep],
  )

  const scrollPrev = useCallback(() => scrollByCard('prev'), [scrollByCard])
  const scrollNext = useCallback(() => scrollByCard('next'), [scrollByCard])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    updateControls()

    const onScroll = () => updateControls()
    container.addEventListener('scroll', onScroll, {passive: true})

    const resizeObserver = new ResizeObserver(() => updateControls())
    resizeObserver.observe(container)

    return () => {
      container.removeEventListener('scroll', onScroll)
      resizeObserver.disconnect()
    }
  }, [updateControls, itemsCount])

  return {
    containerRef,
    canScrollPrev,
    canScrollNext,
    scrollPrev,
    scrollNext,
  }
}
